
"use client"

import { memo } from "react"
import { Mic, CheckCircle } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card"
import ConversationViewer from "./conversation-viewer"
import { LiveTranscriptionLyrics } from "./live-transcription-lyrics"

function TranscriptionInterfaceImpl({
  transcript,
  liveTranscript,
  transcriptionSource,
  correctedTranscript,
  isRecording,
  isPaused = false,
  isGrammarChecking,
  /** When an upload job is in progress, show the same dark streaming (prompter) UI using WebSocket lines from AudioUploadTab. */
  uploadStreamActive = false,
  uploadStreamLines = null,
  uploadStreamStatus = "",
}) {
  const uploadLineTexts = (uploadStreamLines || [])
    .map((row) => (typeof row?.text === "string" ? row.text : String(row || "")).trim())
    .filter(Boolean)
  const fromUpload = uploadStreamActive
  const resolvedTranscript = fromUpload
    ? uploadLineTexts.length > 1
      ? uploadLineTexts.slice(0, -1).join("\n")
      : ""
    : transcript || ""
  const resolvedLive = fromUpload
    ? uploadLineTexts.length
      ? uploadLineTexts[uploadLineTexts.length - 1]
      : ""
    : liveTranscript || ""

  const useAzurePrompterLayout =
    transcriptionSource === "azure" || transcriptionSource === "deepgram" || fromUpload
  const transcriptForDisplay = fromUpload ? resolvedTranscript : (transcript || "")
  const hasLineFormattedTranscript =
    transcriptForDisplay.includes("\n") ||
    /\[translated from Spanish\]/i.test(transcriptForDisplay)
  const showLyricsPanel =
    isRecording ||
    fromUpload ||
    !!(resolvedLive && resolvedLive.trim()) ||
    !!(fromUpload && uploadLineTexts.length > 0) ||
    (!isRecording && hasLineFormattedTranscript && !!transcriptForDisplay.trim())
  const showTranscribing = isRecording || fromUpload

  return (
    <div className="space-y-4 h-full flex flex-col">
      {/* Grammar Status */}
      {(isGrammarChecking || correctedTranscript) && (
        <div className="flex items-center space-x-2">
          {isGrammarChecking ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <span className="text-base text-blue-700 font-medium">Checking grammar…</span>
            </>
          ) : (
            <>
              <CheckCircle size={18} className="text-green-600 shrink-0" />
              <span className="text-base text-green-700 font-medium">Grammar checked</span>
            </>
          )}
        </div>
      )}

      {/* Transcription Display */}
      <Card className="flex-1 relative border border-gray-200 flex flex-col min-h-0 shadow-sm">
        {showTranscribing && (
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center justify-between gap-3 text-lg font-semibold text-gray-900">
              <span className="flex items-center gap-2 flex-wrap">
                {((isRecording &&
                  (transcriptionSource === "azure" || transcriptionSource === "deepgram")) ||
                  (fromUpload && useAzurePrompterLayout)) && (
                  <span className="text-sm font-medium text-blue-800 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-md">
                    {fromUpload && !isRecording
                      ? "Upload streaming"
                      : transcriptionSource === "deepgram"
                        ? "Deepgram streaming"
                        : "Azure streaming"}
                  </span>
                )}
                {isRecording && transcriptionSource === "browser" && (
                  <span className="text-sm font-medium text-gray-700 bg-gray-100 border border-gray-200 px-2.5 py-1 rounded-md">
                    Browser speech
                  </span>
                )}
              </span>
              <div className="flex items-center space-x-2">
                {isRecording && (
                  <div className="flex items-center space-x-2">
                    <div
                      className={`w-3 h-3 rounded-full shrink-0 ${
                        isPaused
                          ? "bg-amber-500"
                          : "bg-red-500 animate-pulse"
                      }`}
                    />
                    <span
                      className={`text-base font-medium ${
                        isPaused ? "text-amber-600" : "text-red-600"
                      }`}
                    >
                      {isPaused ? "Paused" : "Recording…"}
                    </span>
                  </div>
                )}
                {fromUpload && !isRecording && (
                  <div className="flex items-center space-x-2 max-w-[min(28rem,55vw)] min-w-0">
                    <div className="w-3 h-3 bg-sky-500 rounded-full animate-pulse shrink-0" />
                    <span className="text-base font-medium text-sky-700 truncate" title={uploadStreamStatus || "Transcribing file…"}>
                      {uploadStreamStatus || "Transcribing file…"}
                    </span>
                  </div>
                )}
              </div>
            </CardTitle>
          </CardHeader>
        )}
        <CardContent className={`flex-1 flex flex-col min-h-0 ${showTranscribing ? "pt-0" : "pt-6"}`}>
           <div className="min-h-[240px] p-4 md:p-5 border border-gray-100 rounded-xl bg-gray-50/80 flex-1">
            {showLyricsPanel ? (
              <LiveTranscriptionLyrics
                transcript={resolvedTranscript}
                liveTranscript={resolvedLive}
                isRecording={isRecording}
                isUploadStreaming={fromUpload && !isRecording}
                simplified
              />
            ) : (correctedTranscript || transcript) ? (
              <ConversationViewer conversation={correctedTranscript || transcript} />
            ) : (
              <div className="text-center py-14 text-gray-600 relative px-2">
                <Mic size={52} className="mx-auto mb-4 text-gray-300" aria-hidden />
                <p className="font-semibold text-lg text-gray-800">Start this visit from the header</p>
                <p className="text-base mt-3 max-w-md mx-auto leading-relaxed">
                  Your text will appear here in a large, easy-to-read view while you dictate.
                </p>
                <p className="text-base mt-2 max-w-md mx-auto leading-relaxed text-gray-600">
                  Grammar check runs on the full text when you stop.
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export const TranscriptionInterface = memo(TranscriptionInterfaceImpl)